import {
  HOTEL_NAME_UNAVAILABLE,
  resolveOrderHotelNameForDisplay,
} from "../../../utils/orders/orderHotelNameSsot";
import { formatHrBookingRef } from "../../../utils/bookingRefDisplay.js";
import {
  buildCrmLinkedBookingFinancialFromOrder,
  buildCrmPaymentsReceivedFromOrder,
} from "./crmLinkedBookingFinancial";

/**
 * Extract LDS order id from a CRM lead row (source_url / internal note).
 */
export function extractOrderIdFromCrmLead(lead) {
  if (!lead || typeof lead !== "object") return null;

  const sourceKey = String(lead.source_key || "").trim();
  if (sourceKey.startsWith("lds_orders:")) {
    const id = sourceKey.slice("lds_orders:".length).trim();
    if (id) return id;
  }

  const sourceUrl = String(lead.source_url || "");
  const urlMatch = sourceUrl.match(/orderId=([^&/\s]+)/i);
  if (urlMatch?.[1]) return decodeURIComponent(urlMatch[1]).trim();

  const note = String(lead.agent_internal_note || "");
  const noteMatch = note.match(/order_id:\s*([0-9a-f-]{36})/i);
  if (noteMatch?.[1]) return noteMatch[1].trim();

  return null;
}

/**
 * Extract ops-request id from a general (order-less) CRM docket lead.
 */
export function extractOpsRequestIdFromCrmLead(lead) {
  if (!lead || typeof lead !== "object") return null;

  const fromLinked = String(
    lead.linked_booking?.ops_request_id || lead.linked_booking?.opsRequestId || ""
  ).trim();
  if (fromLinked) return fromLinked;

  const sourceKey = String(lead.source_key || "").trim();
  if (sourceKey.startsWith("ops_request:")) {
    const id = sourceKey.slice("ops_request:".length).trim();
    if (id) return id;
  }

  const sourceUrl = String(lead.source_url || "");
  const urlMatch = sourceUrl.match(/opsRequestId=([^&/\s]+)/i);
  if (urlMatch?.[1]) return decodeURIComponent(urlMatch[1]).trim();

  const note = String(lead.agent_internal_note || "");
  const noteMatch = note.match(/ops_request_id:\s*([0-9a-f-]{36})/i);
  if (noteMatch?.[1]) return noteMatch[1].trim();

  return null;
}

function firstAdult(order) {
  const pax = order?.passengersinfo;
  const rooms = Array.isArray(pax) ? pax : [];
  for (const room of rooms) {
    const adults = room?.adults;
    if (Array.isArray(adults) && adults[0]) return adults[0];
  }
  return {};
}

function countPax(order) {
  const rooms = Array.isArray(order?.passengersinfo) ? order.passengersinfo : [];
  let adults = 0;
  let kids = 0;
  let infants = 0;
  for (const room of rooms) {
    adults += Array.isArray(room?.adults) ? room.adults.length : 0;
    kids += Array.isArray(room?.kids)
      ? room.kids.length
      : Array.isArray(room?.children)
        ? room.children.length
        : 0;
    infants += Array.isArray(room?.infants) ? room.infants.length : 0;
  }
  const parts = [];
  if (adults) parts.push(`${adults} מבוגרים`);
  if (kids) parts.push(`${kids} ילדים`);
  if (infants) parts.push(`${infants} תינוקות`);
  return parts.join(" · ") || "—";
}

function hotelNameFromOrder(order) {
  const ssot = resolveOrderHotelNameForDisplay(order);
  const ssotName = String(ssot?.hotelName || "").trim();
  if (ssotName && ssotName !== HOTEL_NAME_UNAVAILABLE) return ssotName;

  const direct =
    order?.hotelname || order?.hotelName || order?.hotel_name || "";
  if (String(direct).trim()) return String(direct).trim();

  const rooms = Array.isArray(order?.passengersinfo) ? order.passengersinfo : [];
  for (const room of rooms) {
    const hn = room?.room?.hotelName || room?.room?.hotelname;
    if (hn) return String(hn).trim();
    const rm = room?.room && typeof room.room === "object" ? room.room : {};
    for (const key of ["dealId", "ldsDealId", "deal_id"]) {
      const blob = decodeDealBlob(rm[key] || room?.[key]);
      const fromBlob = String(
        blob?.hotelName || blob?.hotel_name || blob?.HotelName || ""
      ).trim();
      if (fromBlob) return fromBlob;
    }
  }

  const linked = order?.linkedflights || order?.linkedFlights;
  if (linked && typeof linked === "object") {
    const blob = decodeDealBlob(linked.ldsDealId || linked.dealId);
    const fromLinked = String(
      blob?.hotelName || blob?.hotel_name || ""
    ).trim();
    if (fromLinked) return fromLinked;
  }

  return "";
}

function isVacationPackageDeal(order) {
  const dealtype = String(order?.dealtype || order?.dealType || "").trim().toLowerCase();
  return (
    ["flightshotel", "vacation", "vacationpackage", "package"].includes(dealtype) ||
    (dealtype.includes("flight") && dealtype.includes("hotel"))
  );
}

function dealTypeLabelFromOrder(order) {
  const dealtype = String(order?.dealtype || order?.dealType || "").trim();
  const low = dealtype.toLowerCase();
  if (isVacationPackageDeal(order)) return "חבילת נופש";
  if (low === "hotel" || low === "hotels") return "מלון";
  if (low === "flight" || low === "flights") return "טיסה";
  return dealtype || "LDS";
}

function destinationLabelFromOrder(order, hotel) {
  let city = "";
  const linked = order?.linkedflights || order?.linkedFlights;
  if (linked && typeof linked === "object") {
    const flights = linked.flight || [];
    if (Array.isArray(flights) && flights[0]?.leg) {
      city = String(flights[0].leg.arrCity || "").trim();
    }
    if (!city) {
      const blob = decodeDealBlob(linked.ldsDealId || linked.dealId);
      city = String(
        blob?.hotelCity || blob?.cityCodeArr || blob?.city || ""
      ).trim();
    }
  }
  if (!city) {
    city = String(order?.dest || order?.destination || order?.city || "").trim();
  }

  const vacation = isVacationPackageDeal(order);
  if (hotel && city) return vacation ? `${hotel} · ${city}` : hotel;
  if (hotel) return hotel;
  if (city) return vacation ? `חבילת נופש · ${city}` : city;
  if (vacation) return "חבילת נופש";
  return "";
}

function roomsList(order) {
  const rooms = Array.isArray(order?.passengersinfo) ? order.passengersinfo : [];
  return rooms
    .filter((room) => room && typeof room === "object")
    .map((room, idx) => {
      const rm = room.room && typeof room.room === "object" ? room.room : {};
      const roomType = String(
        room.roomType ||
          room.room_type ||
          room.roomName ||
          room.room_name ||
          rm.roomType ||
          rm.room_type ||
          ""
      ).trim();
      const adults = Array.isArray(room.adults) ? room.adults.length : 0;
      const kids = Array.isArray(room.kids)
        ? room.kids.length
        : Array.isArray(room.children)
          ? room.children.length
          : 0;
      const infants = Array.isArray(room.infants) ? room.infants.length : 0;
      const bits = [];
      if (adults) bits.push(adults === 1 ? "מבוגר אחד" : `${adults} מבוגרים`);
      if (kids) bits.push(kids === 1 ? "ילד אחד" : `${kids} ילדים`);
      if (infants) bits.push(infants === 1 ? "תינוק אחד" : `${infants} תינוקות`);
      return {
        index: idx + 1,
        room_type: roomType || `חדר ${idx + 1}`,
        pax_summary: bits.length ? bits.join(" · ") : null,
      };
    });
}

function decodeDealBlob(raw) {
  if (raw && typeof raw === "object") return raw;
  if (!raw || typeof raw !== "string") return null;
  const text = raw.trim();
  if (!text) return null;
  if (text.startsWith("{") || text.startsWith("[")) {
    try {
      const parsed = JSON.parse(text);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
      /* continue */
    }
  }
  try {
    let b64 = text;
    while (b64.length % 4) b64 += "=";
    const parsed = JSON.parse(atob(b64));
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function flightLegs(order) {
  const seen = new Set();
  const legs = [];

  const push = (leg) => {
    if (!leg || typeof leg !== "object") return;
    let depRaw = String(leg.depDate || leg.dep_date || "").trim();
    let arrRaw = String(leg.arrDate || leg.arr_date || "").trim();
    let depTime = String(leg.depTime || leg.dep_time || "").trim();
    let arrTime = String(leg.arrTime || leg.arr_time || "").trim();
    if (!depTime && depRaw.includes(" ")) {
      const [d, t] = depRaw.split(" ", 2);
      depRaw = d;
      depTime = (t || "").trim();
    }
    if (!arrTime && arrRaw.includes(" ")) {
      const [d, t] = arrRaw.split(" ", 2);
      arrRaw = d;
      arrTime = (t || "").trim();
    }
    const fn = String(leg.flightNumber || leg.flight_number || "").trim();
    const airline = String(leg.airline || leg.carrier || "").trim();
    const origin = String(leg.cityCodeDep || leg.from || leg.depCity || "").trim();
    const dest = String(leg.cityCodeArr || leg.to || leg.arrCity || "").trim();
    if (!(fn || origin || dest || depRaw)) return;
    const key = [fn, origin, dest, depRaw, depTime, arrRaw, arrTime].join("|");
    if (seen.has(key)) return;
    seen.add(key);
    legs.push({
      flight_number: fn || null,
      airline: airline || null,
      origin: origin || null,
      destination: dest || null,
      dep_date: depRaw || null,
      dep_time: depTime || null,
      arr_date: arrRaw || null,
      arr_time: arrTime || null,
      route_label: origin && dest ? `${origin} → ${dest}` : origin || dest || null,
      schedule_label:
        [
          depRaw || depTime ? `${depRaw} ${depTime}`.trim() : "",
          arrRaw || arrTime ? `נחיתה ${arrRaw} ${arrTime}`.trim() : "",
        ]
          .filter(Boolean)
          .join(" · ") || null,
    });
  };

  const rooms = Array.isArray(order?.passengersinfo) ? order.passengersinfo : [];
  for (const room of rooms) {
    if (!room || typeof room !== "object") continue;
    const rm = room.room && typeof room.room === "object" ? room.room : {};
    for (const key of ["dealId", "ldsDealId", "deal_id"]) {
      const blob = decodeDealBlob(rm[key] || room[key]);
      const fl = blob?.flightLegs || blob?.flights;
      if (Array.isArray(fl)) fl.forEach(push);
    }
  }
  for (const key of ["flights", "flightLegs", "flight_legs"]) {
    const fl = order?.[key];
    if (Array.isArray(fl)) fl.forEach(push);
  }
  const dealBlob = decodeDealBlob(order?.dealid || order?.dealId);
  if (dealBlob) {
    const fl = dealBlob.flightLegs || dealBlob.flights;
    if (Array.isArray(fl)) fl.forEach(push);
  }
  return legs;
}

function isBankTransfer(order) {
  const keys = ["payment_method", "paymentMethod", "pay_method"];
  for (const key of keys) {
    const val = String(order?.[key] || "").toLowerCase();
    if (val.includes("bank") || ["wire", "transfer", "העברה"].includes(val)) {
      return true;
    }
  }
  if ([true, 1, "true", "True", "1"].includes(order?.bank_transfer)) return true;
  const ext = order?.extended;
  if (ext && typeof ext === "object") {
    if ([true, 1, "true", "True"].includes(ext.bank_transfer)) return true;
    if (String(ext.payment_method || "").toLowerCase().includes("bank")) return true;
  }
  return false;
}

function paymentMetaFromOrder(order, financial) {
  const payments = Array.isArray(order?.payments) ? order.payments : [];
  const last = [...payments].reverse().find((p) => p && typeof p === "object") || null;
  let paidAt = null;
  let authNumber = null;
  let transactionId = null;
  let installments = null;
  if (last) {
    paidAt = last.callback_received_at || last.created_at || last.paymentDate || null;
    authNumber = last.AuthNumber || last.AuthNum || last.auth_number || null;
    const src = String(last.payment_source || last.source || "").trim();
    const hypAuth = last.hyp_trans_id || last.hypTransId || null;
    const captureId =
      last.capture_trans_id ||
      last.hyp_capture_trans_id ||
      last.token_transaction_id ||
      (src === "hyp_j5_soft_capture" ||
      (hypAuth && last.Id && String(last.Id) !== String(hypAuth))
        ? last.Id || last.payment_id
        : null);
    transactionId =
      captureId || hypAuth || last.TransId || last.transaction_id || last.Id || null;
    const tash = Number(last.tash);
    installments = Number.isFinite(tash) && tash > 0 ? tash : null;
  }
  const orderId = String(order?.id || "").trim();
  return {
    paid_at: paidAt ? String(paidAt) : null,
    auth_number: authNumber ? String(authNumber) : null,
    transaction_id: transactionId ? String(transactionId) : null,
    installments,
    confirmation_email_sent: Boolean(order?.client_confirmation_email_sent),
    pdf_endpoint: orderId ? `/lds_orders/${orderId}/download-pdf` : null,
    bank_transfer: isBankTransfer(order),
    charged_amount: financial.amount_paid,
    charged_amount_display: financial.amount_paid_display,
    original_amount: financial.total_due,
    original_amount_display: financial.total_due_display,
    j5_undercharge: false,
  };
}

function buildLiveAiSalesNote({ dealLabel, destination, dates, pax, amount, status }) {
  return (
    `הזמנה אוטומטית · ${dealLabel || "הזמנה"} · ${destination || "—"} · ` +
    `תאריכים: ${dates || "—"} · הרכב: ${pax || "—"} · סכום: ${amount || "—"} · ` +
    `סטטוס: ${status || "—"}`
  );
}

function buildLiveCustomerMessage(ref) {
  return `הזמנה שבוצעה במערכת · מס׳ ${ref || "—"}`;
}

function buildLiveInternalNote({
  dealLabel,
  dealType,
  ref,
  orderId,
  destination,
  room,
  dates,
  pax,
  amount,
}) {
  return [
    "תיק נפתח אוטומטית מהזמנה",
    `סוג: ${dealLabel || "הזמנה"} (${dealType || "LDS"})`,
    `מס׳ הזמנה / booking_ref: ${ref || "—"}`,
    `order_id: ${orderId || "—"}`,
    `יעד / מלון: ${destination || "—"}`,
    `חדר: ${room || "—"}`,
    `תאריכים: ${dates || "—"}`,
    `הרכב: ${pax || "—"}`,
    `סכום: ${amount || "—"}`,
  ].join("\n");
}

/**
 * Build linked_booking card from a raw LDS order using order-page financial SSOT.
 */
export function buildLinkedBookingFromLdsOrder(order) {
  if (!order || typeof order !== "object") return null;
  const adult = firstAdult(order);
  const orderId = String(order.id || "").trim();
  if (!orderId) return null;

  const dep = order.dep_date ? String(order.dep_date) : null;
  const ret = order.ret_date ? String(order.ret_date) : null;
  const cancelled = Boolean(order.cancelled);
  const guest = [adult.firstname || adult.firstName, adult.lastname || adult.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  const hotel = hotelNameFromOrder(order);
  const destination = destinationLabelFromOrder(order, hotel);
  const dealLabel = dealTypeLabelFromOrder(order);
  const rooms = roomsList(order);
  const roomTypes = rooms.map((r) => r.room_type).filter(Boolean);
  const roomType =
    roomTypes.length <= 1
      ? roomTypes[0] || null
      : roomTypes.map((t, i) => `${i + 1}. ${t}`).join(" · ");
  const flights = flightLegs(order);
  const financial = buildCrmLinkedBookingFinancialFromOrder(order);
  const paymentsReceived = buildCrmPaymentsReceivedFromOrder(order);
  const receipt = {
    ...financial,
    ...paymentMetaFromOrder(order, financial),
  };

  let statusLabel = financial.status_label || "פתוחה";
  if (cancelled) statusLabel = "מבוטלת";

  return {
    order_type: "lds_orders",
    order_id: orderId,
    booking_ref: order.booking_ref ? String(order.booking_ref) : null,
    deal_type: order.dealtype ? String(order.dealtype) : "LDS",
    deal_type_label: dealLabel,
    hotel_name: hotel || destination || "—",
    interest_destination: destination || hotel || null,
    room_type: roomType,
    rooms,
    flights,
    dep_date: dep,
    ret_date: ret,
    dates_label: [dep, ret].filter(Boolean).join(" – ") || "—",
    pax_summary: countPax(order),
    amount_display: financial.total_due_display || "—",
    paid: Boolean(financial.payment_complete),
    cancelled,
    status_label: statusLabel,
    guest_name: guest || null,
    phone: adult.cell || adult.phone || null,
    email: adult.email ? String(adult.email).trim() : null,
    ai_sales_note: buildLiveAiSalesNote({
      dealLabel,
      destination: destination || hotel,
      dates: [dep, ret].filter(Boolean).join(" – ") || "—",
      pax: countPax(order),
      amount: financial.total_due_display || "—",
      status: statusLabel,
    }),
    message: buildLiveCustomerMessage(order.booking_ref || orderId),
    agent_internal_note: buildLiveInternalNote({
      dealLabel,
      dealType: order.dealtype ? String(order.dealtype) : "LDS",
      ref: order.booking_ref || orderId,
      orderId,
      destination: destination || hotel,
      room: roomType,
      dates: [dep, ret].filter(Boolean).join(" – ") || "—",
      pax: countPax(order),
      amount: financial.total_due_display || "—",
    }),
    payment_receipt: receipt,
    payments_received: paymentsReceived,
    fx_rate: financial.fx_rate,
    fx_rate_display: financial.fx_rate_display,
    amount_usd: financial.amount_usd,
    amount_usd_display: financial.amount_usd_display,
    currency: financial.currency,
    total_due_display: financial.total_due_display,
    balance_due_display: financial.balance_due_display,
    payment_complete: financial.payment_complete,
    hero_amount_label: financial.hero_amount_label,
    hero_amount_display: financial.hero_amount_display,
    baggage_display: financial.baggage_display,
    supplier_net_display: financial.supplier_net_display,
    hr_commission_display: financial.hr_commission_display,
    agent_commission_display: financial.agent_commission_display,
    ssot: true,
  };
}

function fmtCruiseIls(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  return `₪${n.toLocaleString("he-IL", {
    maximumFractionDigits: n % 1 === 0 ? 0 : 2,
  })}`;
}

/**
 * Received-payment rows for Mano cruise dockets — from cruise_payments / order.payments.
 */
export function buildCrmPaymentsReceivedFromCruiseOrder(order) {
  const payments = Array.isArray(order?.payments) ? order.payments : [];
  const rows = [];
  payments.forEach((payment, i) => {
    if (!payment || typeof payment !== "object") return;
    const paid =
      payment.paid === true ||
      String(payment.status || "").toLowerCase() === "completed" ||
      String(payment.status || "").toLowerCase() === "paid";
    if (!paid) return;
    const amountIls = Number(payment.amount || payment.actualAmount || 0);
    if (!(amountIls > 0)) return;
    const tash = Number(payment.tash);
    rows.push({
      key: `cruise-pay-${payment.id || i}`,
      index: i,
      method: "credit_card",
      method_label: "כרטיס אשראי",
      amount_ils: amountIls,
      principal_ils: Number(payment.actualAmount || amountIls),
      interest_ils: Math.max(amountIls - Number(payment.actualAmount || amountIls), 0),
      amount_display: fmtCruiseIls(amountIls),
      paid_at: payment.paidAt || payment.paid_at || payment.createdAt || null,
      installments: Number.isFinite(tash) && tash > 0 ? tash : null,
      transaction_id: payment.transactionId || payment.transaction_id || payment.paymentId || null,
    });
  });
  return rows;
}

/**
 * Overlay live cruise order payment totals onto a Mano CRM linked_booking.
 */
export function mergeCruiseOrderIntoLinkedBooking(linked, order) {
  if (!linked || typeof linked !== "object") return linked;
  if (!order || typeof order !== "object") return linked;
  const cruise = order.cruise || order.cruiseInfo || {};
  const amountIls = Number(
    cruise.amount_ils || order.amount_ils || order.packagepricedetails?.FinalPrice || 0
  );
  const totalPaid = Number(
    order.totalPaid != null ? order.totalPaid : order.sumpaid || 0
  );
  const remaining =
    order.remainingAmount != null
      ? Number(order.remainingAmount)
      : Math.max(amountIls - totalPaid, 0);
  const paid = Boolean(order.paid) || String(order.status || "").toLowerCase() === "paid";
  const complete = paid || (amountIls > 0 && totalPaid + 0.02 >= amountIls);
  const paymentsReceived = buildCrmPaymentsReceivedFromCruiseOrder(order);
  const amountDisplay = fmtCruiseIls(amountIls);
  const paidDisplay = fmtCruiseIls(totalPaid);
  const balanceDisplay = fmtCruiseIls(remaining);
  const heroLabel = complete
    ? "סכום ששולם"
    : totalPaid > 0
      ? "יתרה לתשלום"
      : "סכום הזמנה";
  const heroDisplay = complete
    ? paidDisplay
    : totalPaid > 0
      ? balanceDisplay
      : amountDisplay;
  return {
    ...linked,
    order_id: String(order.id || linked.order_id || ""),
    order_type: "cruise_orders",
    booking_ref: order.booking_ref || order.order_number || linked.booking_ref,
    paid: complete,
    payment_complete: complete,
    status_label: complete ? "שולמה" : linked.status_label || "נסגרה במנו",
    amount_display: amountDisplay,
    total_due_display: amountDisplay,
    balance_due_display: balanceDisplay,
    hero_amount_label: heroLabel,
    hero_amount_display: heroDisplay,
    payments_received: paymentsReceived,
    payment_receipt: {
      ...(linked.payment_receipt || {}),
      total_due_display: amountDisplay,
      balance_due_display: balanceDisplay,
      payment_complete: complete,
      hero_amount_label: heroLabel,
      hero_amount_display: heroDisplay,
      paid_amount_display: paidDisplay,
      currency: "ILS",
    },
    ssot: true,
  };
}

/**
 * Build Operation Request picker `selectedOrder` from CRM `linked_booking`
 * so dockets can deep-link into `/operation-requests/new` without order search.
 */
export function buildPickerOrderFromLinkedBooking(linked) {
  if (!linked || typeof linked !== "object") return null;
  const orderId = String(linked.order_id || "").trim();
  if (!orderId) return null;

  const bookingRef =
    linked.booking_ref != null ? String(linked.booking_ref).trim() : "";
  const displayOrderNumber =
    formatHrBookingRef(bookingRef) ||
    (bookingRef && !/^[0-9a-f-]{36}$/i.test(bookingRef) ? bookingRef : null);

  const orderType = String(linked.order_type || "").toLowerCase();
  let category = "packages";
  let rawOrderType = "lds_package";
  if (orderType.includes("hotel") || orderType === "gimmonix") {
    category = "hotels";
    rawOrderType = "hotel";
  } else if (orderType.includes("dynamic")) {
    category = "dynamic-packages";
    rawOrderType = "dynamic_package";
  } else if (orderType.includes("cruise")) {
    category = "cruise";
    rawOrderType = "cruise";
  } else if (orderType.includes("flight") && !orderType.includes("hotel")) {
    category = "טיסות";
    rawOrderType = "flight";
  } else if (orderType === "lds_orders" || orderType.includes("lds")) {
    category = "packages";
    rawOrderType = "lds_package";
  }

  return {
    key: `crm-linked-${rawOrderType}-${orderId}`,
    id: orderId,
    orderId,
    displayOrderNumber,
    bookingRef: bookingRef || null,
    mainContact: linked.guest_name ? String(linked.guest_name).trim() : "",
    hotelName: linked.hotel_name || "—",
    checkIn: linked.dep_date || "",
    checkOut: linked.ret_date || "",
    status: linked.status_label || "—",
    category,
    rawOrderType,
    raw: {
      booking_ref: bookingRef || null,
      _fromCrmLinkedBooking: true,
    },
  };
}
